/** @jsx jsx */
import React, { type FC } from 'react';
import { css, jsx } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import { LoadingButton } from '@atlaskit/button';
import Form from '@atlaskit/form';
import { Stack, Box } from '@atlaskit/primitives';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { createVirtualAgentSection_servicedeskSettingsVirtualAgentLanding$key } from '@atlassian/jira-relay/src/__generated__/createVirtualAgentSection_servicedeskSettingsVirtualAgentLanding.graphql';
import type { RequestTypeOption } from '@atlassian/jira-servicedesk-settings-virtual-agent-common/src/ui/request-type-select/types.tsx';
import { useCreateVirtualAgentForProject } from '../../../services/create-virtual-agent/create-virtual-agent';
import messages from './messages';
import { RequestTypeSelector } from './request-type-selector';

export const CreateVirtualAgentSection: FC<{
	project: createVirtualAgentSection_servicedeskSettingsVirtualAgentLanding$key;
}> = ({ project }) => {
	const { formatMessage } = useIntl();

	const projectData = useFragment(
		graphql`
			fragment createVirtualAgentSection_servicedeskSettingsVirtualAgentLanding on JiraProject {
				id
			}
		`,
		project,
	);

	const [{ isLoading }, { createVirtualAgentForProject }] = useCreateVirtualAgentForProject(
		projectData.id,
	);

	return (
		<>
			<Form<{
				readonly defaultRequestType: RequestTypeOption | null;
			}>
				onSubmit={async (formData) => {
					if (!formData.defaultRequestType) {
						return { defaultRequestType: 'EMPTY' };
					}

					createVirtualAgentForProject(formData.defaultRequestType.id);

					return undefined;
				}}
			>
				{({ formProps, submitting }) => (
					<form {...formProps} css={formStyles}>
						<Stack space="space.200">
							<RequestTypeSelector />
							<Box as="span">
								<LoadingButton
									appearance="primary"
									type="submit"
									isLoading={isLoading || submitting}
									onClick={(_event, analyticsEvent) => {
										// https://data-portal.internal.atlassian.com/analytics/registry/58034
										fireUIAnalytics(analyticsEvent, 'virtualAgentGetStarted');
									}}
									testId="servicedesk-settings-virtual-agent-landing.ui.action-section.create-virtual-agent-section.create-button"
								>
									{formatMessage(messages.getStartedButton)}
								</LoadingButton>
							</Box>
						</Stack>
					</form>
				)}
			</Form>
		</>
	);
};

const formStyles = css({
	width: '100%',
});
